import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Checkbox, Input, Popover, Typography } from 'antd'
import { isNil } from 'lodash'
import PropTypes from 'prop-types'

import { GridFormItem } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'

import { StyledPaddingBottomRow } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'

import { useUserHasPermissions } from '../../../account/hooks'
import { useDuplicateBrandCurationTaskMutation } from '../../hooks'

const BrandCurationTaskDuplicateButton = ({ isDisabled, originalBrandId }) => {
  const { id } = useParams()
  const [duplicate, { isLoading, error }] =
    useDuplicateBrandCurationTaskMutation()

  const [isPopoverVisible, setIsPopoverVisible] = useState(false)
  const [isDefaultTaskNameUsed, setIsDefaultTaskNameUsed] = useState(true)
  const [customName, setCustomName] = useState('')

  const hasPermissionToDuplicate = useUserHasPermissions(
    PERMISSIONS.BRAND_CURATION_TASK.DUPLICATE,
  )

  const isDuplicationDisabled = useMemo(
    () => !hasPermissionToDuplicate || isDisabled,
    [hasPermissionToDuplicate, isDisabled],
  )

  const handleDuplicate = useCallback(() => {
    duplicate(id, isDefaultTaskNameUsed ? null : customName)
  }, [customName, duplicate, id, isDefaultTaskNameUsed])

  useEffect(() => {
    if (error) {
      showErrorNotification({
        title: 'Failed to duplicate task',
        description: error,
      })
    }
  }, [error])

  const handleCheckboxChange = useCallback(
    e => {
      setIsDefaultTaskNameUsed(e.target.checked)
    },
    [setIsDefaultTaskNameUsed],
  )

  const handleCustomNameChange = useCallback(
    e => {
      setCustomName(e.target.value)
    },
    [setCustomName],
  )

  const popoverContent = useMemo(
    () => (
      <div>
        <StyledPaddingBottomRow>
          <GridFormItem
            span={24}
            label="Use default task name"
            labelAbove={false}
          >
            <Checkbox
              checked={isDefaultTaskNameUsed}
              onChange={handleCheckboxChange}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>
        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={24} label="Custom task name" labelAbove={false}>
            <Input
              value={customName}
              disabled={isDefaultTaskNameUsed}
              placeholder="Enter name..."
              onChange={handleCustomNameChange}
            />
          </GridFormItem>
        </StyledPaddingBottomRow>

        <Button type="primary" onClick={handleDuplicate} loading={isLoading}>
          Duplicate
        </Button>
      </div>
    ),
    [
      isDefaultTaskNameUsed,
      handleCheckboxChange,
      customName,
      handleCustomNameChange,
      handleDuplicate,
      isLoading,
    ],
  )

  if (!isNil(originalBrandId)) {
    return (
      <Typography.Text>This task belongs to a duplicated brand</Typography.Text>
    )
  }

  return (
    <Popover
      visible={isPopoverVisible}
      onVisibleChange={setIsPopoverVisible}
      content={popoverContent}
      trigger="click"
    >
      <Button disabled={isDuplicationDisabled} loading={isLoading}>
        Duplicate task
      </Button>
    </Popover>
  )
}

BrandCurationTaskDuplicateButton.propTypes = {
  isDisabled: PropTypes.bool,
  originalBrandId: PropTypes.string,
}

BrandCurationTaskDuplicateButton.defaultProps = {
  isDisabled: false,
  originalBrandId: undefined,
}

export default BrandCurationTaskDuplicateButton
